import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, AlertOctagon } from "lucide-react";
import { ASSETS, api, adminApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const fmtTime = (iso) =>
  new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const KINDS = {
  ticket: {
    get: (id) => `/tickets/verify/${id}`,
    checkIn: (id) => `/staff/tickets/${id}/check-in`,
    noun: "Ticket",
    normalize: (d) => ({ ...d, id: d.ticket_id, name: d.buyer_name, count: d.quantity, what: d.event_title, date: d.event_date, time: d.event_time, status: d.payment_status }),
    countLabel: (n) => `Admit ${n}`,
  },
  rsvp: {
    get: (id) => `/reservations/${id}/pass`,
    checkIn: (id) => `/staff/reservations/${id}/check-in`,
    noun: "RSVP",
    normalize: (d) => ({ ...d, count: d.party_size, what: d.event_title || "Table reservation" }),
    countLabel: (n) => `Party of ${n}`,
  },
};

const Details = ({ t, k }) => (
  <div className="text-sm space-y-1 mt-4">
    <div data-testid="ticket-verify-name" className="text-xl font-semibold">{t.name}</div>
    <div data-testid="ticket-verify-qty" className="text-lg">{k.countLabel(t.count)}</div>
    <div className="opacity-70">{t.what}</div>
    {t.date && <div className="opacity-70">{t.date} {t.time ? `· ${t.time}` : ""}</div>}
    <div className="font-mono text-xs opacity-70 pt-2">#{t.id.slice(0, 8).toUpperCase()}</div>
  </div>
);

export default function TicketVerify({ kind = "ticket" }) {
  const k = KINDS[kind];
  const { id } = useParams();
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [justChecked, setJustChecked] = useState(false);
  const isStaff = !!localStorage.getItem("trio_admin_token");

  const load = () =>
    api.get(k.get(id))
      .then(({ data }) => setState({ data: k.normalize(data) }))
      .catch(() => setState({ notFound: true }));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkIn() {
    setBusy(true);
    try {
      await adminApi.post(k.checkIn(id));
      setJustChecked(true);
      toast.success("Checked in");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
      load();
    }
  }

  const t = state.data;
  const used = t?.checked_in_at && !justChecked;
  const ok = t?.valid && !used;
  const bg = state.loading ? "bg-[var(--trio-bg)]" : used ? "bg-red-600 text-white" : ok ? "bg-green-50" : "bg-red-50";

  return (
    <div data-testid="ticket-verify-page" className={`min-h-screen flex flex-col items-center justify-center px-6 py-16 text-center ${bg}`}>
      <img src={ASSETS.logo} alt="Trio" className="h-12 w-auto mb-6" />
      {state.loading && <Loader2 className="w-12 h-12 animate-spin text-[var(--trio-primary)]" />}

      {used && (
        <div data-testid="ticket-verify-already" className="max-w-sm w-full">
          <AlertOctagon className="w-24 h-24 mx-auto mb-4" />
          <h1 className="font-display uppercase text-5xl tracking-tight leading-none mb-3">Already Checked In</h1>
          <div data-testid="ticket-verify-checked-at" className="text-lg font-semibold">Used {fmtTime(t.checked_in_at)}</div>
          {t.checked_in_by && <div className="text-sm opacity-80">by {t.checked_in_by}</div>}
          <Details t={t} k={k} />
        </div>
      )}

      {!state.loading && !used && (
        <div data-testid={ok ? "ticket-verify-valid" : "ticket-verify-invalid"} className="max-w-sm w-full">
          {ok ? <CheckCircle2 className="w-20 h-20 mx-auto text-green-600 mb-4" /> : <XCircle className="w-20 h-20 mx-auto text-red-600 mb-4" />}
          <h1 className="font-display uppercase text-4xl tracking-tight mb-2">
            {justChecked ? "Checked In" : ok ? `Valid ${k.noun}` : state.notFound ? `${k.noun} Not Found` : "Not Valid"}
          </h1>
          {t && <Details t={t} k={k} />}
          {t && !t.valid && <div className="text-red-700 uppercase text-xs tracking-widest mt-2">Status: {t.status}</div>}
          {ok && isStaff && !t.checked_in_at && (
            <button
              onClick={checkIn}
              disabled={busy}
              data-testid="ticket-check-in-btn"
              className="mt-8 w-full bg-green-600 hover:bg-green-700 text-white py-5 text-lg tracking-widest uppercase font-bold disabled:opacity-60"
            >
              {busy ? "Checking In…" : `Check In · ${k.countLabel(t.count)}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
